import { processWithConcurrency } from 'utils/async';
import produce from 'utils/produce';
import round from 'utils/round';
import uuid from 'utils/uuid';
import uniqBy from 'utils/uniqBy';

import { getAssetUrl, getPageSize, getTemplatePage } from 'editor-utils/template';
import ancestryApi from '../api/ancestry';
import ancestorsApi from '../api/ancestors';
import generatorApi, { getDirectionFromTreeType } from '../api/generator';
import imageApi from '../api/image';
import { extractPhotosFromPages, generatePage, transformPagesFromServer } from './utils';
import { getAsynchronouslyGeneratedData } from './socket';

/**
 * Avatar Cache.
 * @param {string} accessToken
 * @param {object} template
 * @param {Array} availablePersons
 * @returns {object}
 */
const avatarCache = (accessToken, template, availablePersons = []) => {
  const cache = {};

  return {
    getAvatar: async person => {
      if (!cache[person.id]) {
        const ancestryPerson = availablePersons.find(p => p.id === person.id);
        if (ancestryPerson?.avatarUrl) {
          const { data: imageData } = await imageApi(accessToken).uploadImage(
            ancestryPerson.avatarUrl,
          );
          if (imageData) {
            cache[person.id] = {
              key: uuid(),
              url: imageData.publicUrl,
              validDomain: true,
              alive: 1,
              datetime: 'N/A',
            };

            return cache[person.id];
          }
        }

        const defaultValue = person.gender === 'f' ? 'female-avatar.png' : 'male-avatar.png';

        cache[person.id] = {
          defaultValue,
          key: defaultValue,
          url: getAssetUrl(template, `/assets_client/images/${defaultValue}`),
          validDomain: true,
          alive: 1,
          datetime: 'N/A',
        };
      }
      return cache[person.id];
    },
  };
};

/**
 * Calculate Frame based on pageSize and some padding defined for each section.
 * @param {number} pageWidth
 * @param {number} pageHeight
 * @param {object} minPadding
 * @param {number} ratio
 * @returns {Array}
 */
const calculateFrame = (pageWidth, pageHeight, minPadding, ratio) => {
  const maxWidth = pageWidth - minPadding.left - minPadding.right;
  const maxHeight = pageHeight - minPadding.top - minPadding.bottom;

  let width;
  let height;

  if (ratio > 1) {
    // landscape
    width = maxWidth;
    height = width / ratio;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * ratio;
    }
  } else {
    // portrait
    height = maxHeight;
    width = height * ratio;
    if (width > maxWidth) {
      width = maxWidth;
      height = width / ratio;
    }
  }

  return [
    minPadding.left + (pageWidth - width - minPadding.left - minPadding.right) / 2,
    minPadding.top + (pageHeight - height - minPadding.top - minPadding.bottom) / 2,
    width,
    height,
  ].map(value => round(value, 2));
};


/**
 * Generate Records.
 * @param {string} accessToken
 * @param {object} template
 * @param {Array} ancestryRecords
 * @param {Array} ancestryPersons
 * @returns {{ data: { pages: Array, photos: Array }}}
 */
export const generateAncestryRecordsData = async (
  accessToken,
  template,
  ancestryRecords,
  ancestryPersons,
) => {
  const photos = [];
  const pages = [];

  const templatePage = getTemplatePage(template, 'record');
  const [pageWidth, pageHeight] = getPageSize(template);

  const minPadding = {
    top: 0.15 * pageHeight,
    right: 0.25 * pageWidth,
    bottom: 0.2 * pageHeight,
    left: 0.05 * pageWidth,
  };

  const avatars = avatarCache(accessToken, template, ancestryPersons);

  const processRecord = async (record) => {
    const page = generatePage(templatePage);

    const { data: imageData, error } = await imageApi(accessToken).uploadImage(record.url);
    if (error) {
      console.error(`Wrong upload ${record.url}`);
      return;
    }

    const recordMedium = {
      key: uuid(),
      url: imageData.publicUrl,
      alive: 1,
      validDomain: true,
      datetime: 'N/A',
    };

    const avatarMedium = await avatars.getAvatar(record.person);

    const sourceInfo = ['Source', record.sourceCitation ?? ''].join(': ');

    const description = record.displayFields
      .filter(f => f.Label !== 'Name')
      .map(f => `${f.Label}: ${f.Value}`);

    const recordPlaceholder = {
      id: uuid(),
      type: 'photo',
      frame: calculateFrame(
        pageWidth,
        pageHeight,
        minPadding,
        record.previewWidth / record.previewHeight,
      ),
    };

    const processedPage = produce(page, draft => {
      draft.caption = record.primaryName;
      draft.tags = [record.person.id];

      const placeholderData = placeholder => {
        switch (placeholder.id) {
          case 'page-caption':
            return { value: record.primaryName };
          case 'sub-heading':
            return { value: record.collectionTitle };
          case 'name':
            return { value: record.personName };
          case 'source':
            return { value: sourceInfo };
          case 'description':
            return { value: description.join('\n') };
          case 'photo':
            return {
              photo_key: avatarMedium.key,
              x: 0.5,
              y: 0.5,
              scale: 1,
              orientation: 0,
            };
          default:
            return placeholder.data;
        }
      };

      page.placeholders.forEach((placeholder, placeholderIndex) => {
        draft.placeholders[placeholderIndex].data = placeholderData(placeholder);
      });

      draft.placeholders.push({
        id: recordPlaceholder.id,
        type: 'photo',
        data: {
          photo_key: recordMedium.key,
          x: 0.5,
          y: 0.5,
          scale: 1,
          orientation: 0,
        },
        override: recordPlaceholder,
      });
    });

    return {
      page: processedPage,
      photos: [recordMedium, avatarMedium]
    };
  };

  const results = await processWithConcurrency(ancestryRecords, 2, processRecord);

  // Extract pages and photos from results
  results.forEach(result => {
    if (result) {
      pages.push(result.page);
      photos.push(...result.photos);
    }
  });

  return {
    data: {
      pages,
      photos,
    },
  };
};

/**
 * Generate Photo Pages.
 * @param {string} accessToken
 * @param {object} template
 * @param {Array} ancestryPhotos
 * @returns {{ data: { pages: Array, photos: Array }}}
 */
export const generateAncestryPhotosData = async (accessToken, template, ancestryPhotos) => {
  const photos = [];
  const pages = [];

  const templatePage = getTemplatePage(template, 'blank');
  const [pageWidth, pageHeight] = getPageSize(template);

  const minPadding = {
    top: 0.15 * pageHeight,
    right: 0.05 * pageWidth,
    bottom: 0.1 * pageHeight,
    left: 0.05 * pageWidth,
  };

  const processPhoto = async (photo) => {
    const page = generatePage(templatePage);

    const { data: imageData, error } = await imageApi(accessToken).uploadImage(photo.url);
    if (error) {
      console.error(`Wrong upload ${photo.url}`);
      return;
    }

    const photoMedium = {
      key: uuid(),
      url: imageData.publicUrl,
      alive: 1,
      validDomain: true,
      datetime: 'N/A',
    };

    const photoPlaceholder = {
      id: uuid(),
      type: 'photo',
      frame: calculateFrame(
        pageWidth,
        pageHeight,
        minPadding,
        photo.previewWidth / photo.previewHeight,
      ),
    };

    const processedPage = produce(page, draft => {
      if (photo.person) {
        draft.caption = `${photo.person.name} - Photo`;
        draft.tags = [photo.person.id];
      } else {
        draft.caption = `${photo.name}`;
        draft.tags = []
      }

      const placeholderData = placeholder => {
        switch (placeholder.id) {
          case 'page-caption':
            return { value: photo.person ? photo.person.name : photo.name };
          default:
            return placeholder.data;
        }
      };

      page.placeholders.forEach((placeholder, placeholderIndex) => {
        draft.placeholders[placeholderIndex].data = placeholderData(placeholder);
      });

      draft.placeholders.push({
        id: photoPlaceholder.id,
        type: 'photo',
        data: {
          photo_key: photoMedium.key,
          x: 0.5,
          y: 0.5,
          scale: 1,
          orientation: 0,
        },
        override: photoPlaceholder,
      });
    });

    return {
      page: processedPage,
      photo: photoMedium
    };
  };

  const results = await processWithConcurrency(ancestryPhotos, 2, processPhoto);

  // Extract pages and photos from results
  results.forEach(result => {
    if (result) {
      pages.push(result.page);
      photos.push(result.photo);
    }
  });

  return {
    data: {
      pages,
      photos,
    },
  };
};

/**
 * Generate data from Ancestry source.
 * @param {string} accessToken
 * @param {object} template
 * @param {object} ancestryAuth
 * @param {object} config
 * @param {string} config.type
 * @param {string} config.treeId
 * @param {string} config.treeName
 * @param {string} config.treeType
 * @param {string} config.personId
 * @param {string} config.partnerId
 * @param {Array<Array<string>>} config.arrayOfPersonIds
 * @param {number} config.numberOfGenerations
 * @param {Array<string>} config.optionIds
 * @param {boolean} config.withoutCover
 * @param {Array<object>} currentPhotos
 * @param {void} onProgress
 * @returns {{ data: object, error: object }}
 */
const generateAncestryData = async (
  accessToken,
  template,
  ancestryAuth,
  config = {},
  currentPhotos = [],
  onProgress = () => { },
  onDelayed = (delayInSeconds) => { },
) => {
  const { postLog } = ancestorsApi(config.type, accessToken);

  const arrayOfPersons = [];
  if (config.arrayOfPersonIds) {
    arrayOfPersons.push(...config.arrayOfPersonIds);
  } else {
    arrayOfPersons.push([config.personId, config.partnerId].filter(id => !!id));
  }

  const direction = getDirectionFromTreeType(config.treeType, template.id);

  const { data: socketData, ancestryToken, error: socketError } = await generatorApi(
    accessToken,
    template,
    true,
  ).getAncestryData(
    config.type,
    ancestryAuth,
    config.optionIds,
    arrayOfPersons.map(persons => ({
      persons,
      numberOfGenerations: config.numberOfGenerations,
      treeName: config.treeName,
      direction,
    })),
  );

  if (socketError) {
    postLog(
      `[${socketError.code}] Problem with ${config.type} async generator`,
      {
        sku: template.id,
        version: template.version,
        persons: arrayOfPersons,
        optionIds: config.optionIds,
        numberOfGenerations: config.numberOfGenerations,
        direction,
        [`${config.type}Token`]: ancestryToken,
      },
      socketError.body,
    );

    return {
      error:
        `We encountered an error while creating your project with the data from ${config.type.toUpperCase()} and our team has been automatically notified. We are actively working with ${config.type.toUpperCase()} to identify this data error and will contact you once you will be able to try again.`,
    };
  }

  const { data: dataArray, error } = await getAsynchronouslyGeneratedData(
    socketData.url,
    onProgress,
    onDelayed,
  );

  if (error) {
    postLog(
      `[WSS] Problem with ${config.type} async generator`,
      {
        sessionId: socketData.id,
        sku: template.id,
        version: template.version,
        persons: arrayOfPersons,
        optionIds: config.optionIds,
        numberOfGenerations: config.numberOfGenerations,
        direction,
        [`${config.type}Token`]: ancestryToken,
      },
      error,
    );

    return { error };
  }

  if (dataArray.length === 0 || dataArray.length !== arrayOfPersons.length) {
    postLog(
      `Generator returns only ${dataArray.length} data items, but was required ${arrayOfPersons.length} items.`,
      {
        sessionId: socketData.id,
        sku: template.id,
        version: template.version,
        persons: arrayOfPersons,
        optionIds: config.optionIds,
        numberOfGenerations: config.numberOfGenerations,
        direction,
        [`${config.type}Token`]: ancestryToken,
      },
    );

    return {
      error:
        `We encountered an error while creating your project with the data from ${config.type.toUpperCase()} and our team has been automatically notified. We are actively working with ${config.type.toUpperCase()} to identify this data error and will contact you once you will be able to try again.`,
    };
  }

  const data = {
    cover: dataArray[0].cover,
    pages: dataArray.reduce((acc, item) => {
      if (item.pages) {
        acc.push(...item.pages);
      }
      return acc;
    }, []),
  };

  const withCover = data.cover && config.withoutCover !== true;

  let photos = [...currentPhotos];
  if (withCover) {
    photos.push(...extractPhotosFromPages(template, [data.cover]));
  }
  if (data.pages) {
    photos.push(...extractPhotosFromPages(template, data.pages));
  }

  photos = uniqBy(photos, 'key');

  const pages = [];
  if (withCover) {
    pages.push(...transformPagesFromServer(template, 'cover', photos, [data.cover]));
  }
  if (data.pages) {
    pages.push(...transformPagesFromServer(template, 'pages', photos, data.pages));
  }

  if (pages.length === 0) {
    return {
      error: `Oops! We didn't find anything in ${config.type.toUpperCase()}.`,
    };
  }

  return {
    data: {
      additionalInfo: {
        source: config.type,
        treeId: config.treeId,
        treeName: config.treeName,
        optionIds: config.optionIds || undefined,
      },
      photos,
      pages,
    },
    session: {
      id: socketData.id,
    }
  };
};

export default generateAncestryData;
